/**
 * Engine-wide numeric constants. Three-free, side-effect free.
 * See ARCHITECTURE.md §1 (lifecycle) and §15 (coordinates and units).
 */

/** Simulation runs at exactly 120 Hz. */
export const TICK_RATE = 120;
/** Seconds per fixed simulation step. */
export const FIXED_STEP = 1 / TICK_RATE;

/**
 * Fighting-game frame data is authored and displayed at 60 Hz (the convention players expect),
 * then converted to simulation ticks. One f60 frame == 2 simulation ticks.
 */
export const F60_TO_TICKS = TICK_RATE / 60;

/** A render frame never accumulates more than this much wall time (ms). */
export const MAX_FRAME_MS = 250;
/** Hard cap on catch-up steps per render frame; prevents a simulation death spiral. */
export const MAX_STEPS_PER_FRAME = 8;

// ---------------------------------------------------------------------------
// Stage geometry (metres). The combat axis is X, depth is Z, up is Y.
// ---------------------------------------------------------------------------

export const STAGE_HALF_WIDTH = 5.6;
export const STAGE_HALF_DEPTH = 1.9;
export const GROUND_Y = 0;

/** Downward acceleration for grounded arcs, m/s^2. Juggles scale this per move. */
export const GRAVITY = -22.0;

/** Fighter spawn offsets from stage centre. */
export const SPAWN_X = 1.05;

/** Distance at which fighters are considered to be in close-range pressure. */
export const CLOSE_RANGE = 0.95;
/** Beyond this, neither fighter's fastest mid can reach. */
export const FAR_RANGE = 2.6;

// ---------------------------------------------------------------------------
// Round rules
// ---------------------------------------------------------------------------

export const ROUND_SECONDS = 60;
export const ROUND_TICKS = ROUND_SECONDS * TICK_RATE;
export const BEST_OF = 3;
/** Ticks of the "Round N / Fight" intro before inputs are accepted. */
export const ROUND_INTRO_TICKS = 150;
/** Ticks after a KO before the round formally ends (lets the finisher play out). */
export const ROUND_OUTRO_TICKS = 260;

// ---------------------------------------------------------------------------
// Input
// ---------------------------------------------------------------------------

/** Input buffer depth in simulation ticks (~10 f60 frames). */
export const INPUT_BUFFER_TICKS = 20;
/** Motion-command recognition window in ticks. */
export const COMMAND_WINDOW_TICKS = 24;
/** Directional deadzone for analogue sticks. */
export const STICK_DEADZONE = 0.34;

// ---------------------------------------------------------------------------
// Combat tuning shared across subsystems
// ---------------------------------------------------------------------------

/** Global hitstop (freeze) in ticks by impact weight. */
export const HITSTOP = { light: 6, medium: 10, heavy: 16, counter: 20, throw: 8, wall: 18 };
/** Minimum combo damage multiplier; combos can never scale below this. */
export const MIN_COMBO_SCALE = 0.2;
/** Ticks a throw can be broken after the grab connects. */
export const THROW_BREAK_TICKS = 14;
/** Speed (m/s) into a wall required to produce a wall splat rather than a bump. */
export const WALL_SPLAT_SPEED = 3.4;

/**
 * Hard ceiling on juggle height, metres. The highest legitimate launcher reaches ~2.0 m, so this
 * is a backstop against a data error or an unforeseen interaction carrying a fighter off-screen,
 * not a gameplay tuning value. Reaching it is a bug; `tools/combat-test.mjs` asserts it never is.
 */
export const JUGGLE_CEILING = 3.2;

export const EPSILON = 1e-6;
